import { z } from 'zod';
import { idSchema, instanceIdSchema, propertyMapSchema } from './common.schema';
import { collectorHealthSchema, COLLECTOR_PLATFORMS } from './collector.schema';
import { MAX_TIMESTAMP_MS } from '../utils/row-key';
import { sensorKeySchema, datapointNameSchema } from './sensor.schema';
/**
 * Wire-level identifier constraints aligned with the storage row-key
 * contract (`utils/row-key.ts`) via the shared canonical schemas: what
 * validates at ingest MUST be key-buildable — no late failures at write.
 */
const sensorKeyWireSchema = sensorKeySchema;
const datapointWireSchema = datapointNameSchema;
const epochMsSchema = z.number().int().nonnegative().max(MAX_TIMESTAMP_MS);
/**
 * Ingest wire contracts — what collectors and cloud probes POST to the
 * `ingest` function (tech-arch §5.1). Authenticate + schema-validate +
 * publish to Pub/Sub + 202; no synchronous business logic.
 *
 * Volume caps here are the schema-level abuse guard; per-token rate
 * policy is enforced server-side on top (tech-arch §5.2).
 */
/** One datapoint observation. Timestamps are epoch milliseconds (UTC) —
 *  the wire format favors compactness over ISO strings; the control plane
 *  converts at the boundary. */
export const metricPointSchema = z
    .object({
    resourceId: idSchema,
    /** null for resource-level (non-instanced) datapoints. */
    instanceId: instanceIdSchema.nullable().default(null),
    sensorKey: sensorKeyWireSchema,
    datapoint: datapointWireSchema,
    /** Epoch ms; must be within a sane window (validated server-side
     *  against backfill policy). */
    timestampMs: epochMsSchema,
    value: z.number().finite(),
})
    .strict();
export const telemetryBatchSchema = z
    .object({
    collectorId: idSchema,
    sentAtMs: epochMsSchema,
    /** True when draining the disk-backed queue after an outage —
     *  evaluator treats late data as backfill, not fresh alerts. */
    backfill: z.boolean().default(false),
    metrics: z.array(metricPointSchema).min(1).max(5000),
})
    .strict();
/** Collector heartbeat (also carries self-metrics). */
export const heartbeatSchema = z
    .object({
    collectorId: idSchema,
    version: z.string().min(1).max(64),
    platform: z.enum(COLLECTOR_PLATFORMS),
    startedAtMs: epochMsSchema,
    health: collectorHealthSchema,
    /** Version of the config the collector is currently running. */
    configVersion: z.number().int().nonnegative(),
})
    .strict();
/** Discovered instance reported by a collector discovery cycle. */
export const discoveredInstanceSchema = z
    .object({
    wildValue: z.string().min(1).max(512),
    name: z.string().min(1).max(253),
    properties: propertyMapSchema.optional(),
})
    .strict();
export const discoveryReportSchema = z
    .object({
    collectorId: idSchema,
    resourceId: idSchema,
    sensorKey: sensorKeyWireSchema,
    sensorVersion: z.number().int().min(1),
    completedAtMs: epochMsSchema,
    instances: z.array(discoveredInstanceSchema).max(2000),
    /** Properties set by property sensors on this resource (system.*). */
    resourceProperties: propertyMapSchema.optional(),
})
    .strict();
/** Netscan result — auto-discovery sweep of a range (PRD §2.2). */
export const scanResultEntrySchema = z
    .object({
    address: z.string().min(1).max(253),
    respondedToPing: z.boolean(),
    openPorts: z.array(z.number().int().min(1).max(65535)).max(100).optional(),
    snmpSysObjectId: z.string().max(256).optional(),
    snmpSysName: z.string().max(253).optional(),
    snmpSysDescr: z.string().max(1024).optional(),
})
    .strict();
export const scanReportSchema = z
    .object({
    collectorId: idSchema,
    /** Server-issued scan job id this report answers. */
    scanJobId: idSchema,
    completedAtMs: epochMsSchema,
    results: z.array(scanResultEntrySchema).max(65536),
})
    .strict();
/** Claim-code redemption — how a freshly installed collector registers
 *  (tech-arch §6 fleet management). Returns the per-collector token. */
export const collectorClaimSchema = z
    .object({
    claimCode: z.string().min(16).max(128),
    hostname: z.string().min(1).max(253),
    platform: z.enum(COLLECTOR_PLATFORMS),
    version: z.string().min(1).max(64),
})
    .strict();
//# sourceMappingURL=ingest.schema.js.map